import React from 'react';
import { Box, Paper, Typography, Chip, Stack, IconButton, Button, Tooltip } from '@mui/material';
import {
  ShoppingCart,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Close,
  ShowChart,
  Info,
  Settings,
} from '@mui/icons-material';
import { Recommendation, WatchlistEntry, Position } from '../../types';
import { formatCurrency } from '../../utils/formatters';
import { format } from 'date-fns';

interface MobileRecommendationCardProps {
  recommendation: Recommendation;
  currentWatchlistData?: WatchlistEntry | null;  // Current real-time data from watchlist
  position?: Position | null;  // Position data for exit recommendations (P/L, days held)
  onExecute: (rec: Recommendation) => void;
  onDismiss: (id: string) => void;
  onChartOpen: (symbol: string, pivotPrice: number | undefined, currentPrice: number, priceChangePercent: number) => void;
  onInfoOpen: (symbol: string) => void;
}

const MobileRecommendationCard: React.FC<MobileRecommendationCardProps> = ({
  recommendation: rec,
  currentWatchlistData,
  position,
  onExecute,
  onDismiss,
  onChartOpen,
  onInfoOpen,
}) => {
  // Use current watchlist data if available, otherwise fall back to recommendation data
  const currentPrice = currentWatchlistData?.currentPrice ?? rec.currentPrice;
  const priceChgPercent = currentWatchlistData?.priceChgPercent ?? 0;
  const volumeChgVs50Day = currentWatchlistData?.volumeChgVs50Day;

  // Calculate P/L and days held for exit recommendations
  const calculateDaysHeld = (entryDate: Date): number => {
    const now = new Date();
    const entry = new Date(entryDate);
    const diffTime = Math.abs(now.getTime() - entry.getTime());
    return Math.floor(diffTime / (1000 * 60 * 60 * 24));
  };

  const entryPrice = position?.entryPrice;
  const quantity = position?.quantity || rec.recommendedQuantity;
  const daysHeld = position?.entryDate ? calculateDaysHeld(position.entryDate) : position?.holdingDays;
  const profitLoss = entryPrice && quantity ? (currentPrice - entryPrice) * quantity : undefined;
  const profitLossPercent = entryPrice ? ((currentPrice - entryPrice) / entryPrice) * 100 : undefined;

  const getUrgencyColor = (urgency: string): 'error' | 'warning' | 'info' | 'default' => {
    switch (urgency) {
      case 'immediate': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const getTypeIcon = () => {
    if (rec.type === 'entry') return <ShoppingCart fontSize="small" />;
    if (rec.type === 'exit') return <TrendingDown fontSize="small" />;
    if (rec.type === 'adjustment') return <Settings fontSize="small" />;
    return <Warning fontSize="small" />;
  };

  const getTypeColor = () => {
    if (rec.type === 'entry') return 'success';
    if (rec.type === 'exit') return 'error';
    if (rec.type === 'adjustment') return 'info';
    return 'warning';
  };

  const getBorderColor = () => {
    if (rec.urgency === 'immediate') return 'error.main';
    if (rec.type === 'entry') return 'success.main';
    if (rec.type === 'exit') return 'error.main';
    return 'info.main';
  };

  return (
    <Paper
      elevation={rec.urgency === 'immediate' ? 4 : 1}
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 2,
        borderLeft: '4px solid',
        borderLeftColor: getBorderColor(),
        overflow: 'hidden',
      }}
    >
      {/* Header Row: Symbol, Type Badge, Price Info */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {rec.symbol}
            </Typography>
            <Chip
              label={rec.type}
              size="small"
              color={getTypeColor() as any}
              sx={{ height: 20, fontSize: '0.65rem', '& .MuiChip-icon': { fontSize: 14 } }}
              icon={getTypeIcon()}
            />
            {rec.urgency !== 'low' && (
              <Chip
                label={rec.urgency}
                size="small"
                color={getUrgencyColor(rec.urgency)}
                variant="outlined"
                sx={{ height: 18, fontSize: '0.6rem' }}
              />
            )}
          </Stack>
        </Box>

        {/* Current Price & Change */}
        <Box sx={{ textAlign: 'right', flexShrink: 0, ml: 0.5 }}>
          <Typography variant="body2" fontWeight="medium" fontSize="0.8rem">
            {formatCurrency(currentPrice)}
          </Typography>
          {priceChgPercent !== undefined && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.25}>
              {priceChgPercent >= 0 ? (
                <TrendingUp sx={{ fontSize: 11 }} color="success" />
              ) : (
                <TrendingDown sx={{ fontSize: 11 }} color="error" />
              )}
              <Typography
                variant="caption"
                fontWeight="bold"
                fontSize="0.65rem"
                color={priceChgPercent >= 0 ? 'success.main' : 'error.main'}
              >
                {priceChgPercent >= 0 ? '+' : ''}{priceChgPercent.toFixed(2)}%
              </Typography>
            </Stack>
          )}
        </Box>
      </Box>

      {/* Key Details Row */}
      <Box sx={{ mb: 1 }}>
        {rec.type === 'entry' && (
          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Typography variant="caption">
              Buy {rec.recommendedQuantity} @ {formatCurrency(rec.recommendedEntryPrice || currentPrice)}
            </Typography>
            <Typography variant="caption" color="error.main">
              Stop: {formatCurrency(rec.recommendedStopLoss || 0)}
            </Typography>
            <Typography variant="caption" color="success.main">
              Target: {formatCurrency(rec.recommendedTarget || 0)}
            </Typography>
          </Stack>
        )}
        {rec.type === 'exit' && (
          <Stack spacing={0.5}>
            <Typography variant="caption">
              {rec.exitReason === 'stop_hit' && 'Stop Loss Hit - Exit'}
              {rec.exitReason === 'target_hit' && 'Target Reached - Take Profits'}
              {rec.exitReason === 'time_limit' && 'Holding Time Exceeded'}
              {rec.exitReason === 'stagnant' && 'Position Stagnant'}
            </Typography>
            {/* P/L and Days Held for exit recs */}
            {(profitLoss !== undefined || daysHeld !== undefined) && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap">
                {profitLoss !== undefined && (
                  <Chip
                    label={`${profitLoss >= 0 ? '+' : ''}${formatCurrency(profitLoss)} (${profitLossPercent! >= 0 ? '+' : ''}${profitLossPercent!.toFixed(1)}%)`}
                    size="small"
                    color={profitLoss >= 0 ? 'success' : 'error'}
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
                {daysHeld !== undefined && (
                  <Chip
                    label={`${daysHeld}d held`}
                    size="small"
                    variant="outlined"
                    sx={{ height: 20, fontSize: '0.65rem' }}
                  />
                )}
              </Stack>
            )}
          </Stack>
        )}
        {rec.type === 'adjustment' && (
          <Typography variant="caption">
            {rec.adjustmentType === 'raise_stop' && `Raise stop to ${formatCurrency(rec.adjustmentValue || 0)}`}
            {rec.adjustmentType === 'raise_target' && `Raise target to ${formatCurrency(rec.adjustmentValue || 0)}`}
            {rec.adjustmentType === 'lower_target' && `Lower target to ${formatCurrency(rec.adjustmentValue || 0)}`}
          </Typography>
        )}
      </Box>

      {/* Volume Badge (if available) */}
      {volumeChgVs50Day !== undefined && (
        <Box sx={{ mb: 1 }}>
          <Chip
            label={`Vol: ${volumeChgVs50Day > 0 ? '+' : ''}${volumeChgVs50Day.toFixed(0)}%`}
            size="small"
            color={volumeChgVs50Day > 50 ? 'success' : volumeChgVs50Day > 0 ? 'default' : 'error'}
            variant="outlined"
            sx={{ height: 20 }}
          />
        </Box>
      )}

      {/* Primary Reason */}
      {rec.reasons && rec.reasons.length > 0 && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
          {rec.reasons[0]}
        </Typography>
      )}

      {/* Score & Timestamp */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Chip label={`Score: ${rec.score}`} size="small" variant="outlined" sx={{ height: 20 }} />
        <Typography variant="caption" color="textSecondary">
          {format(rec.generatedAt, 'MMM d, h:mm a')}
        </Typography>
      </Box>

      {/* Actions Row */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" spacing={0}>
          <IconButton
            size="small"
            onClick={() => onChartOpen(rec.symbol, undefined, currentPrice, priceChgPercent || 0)}
            sx={{ p: 0.5 }}
          >
            <ShowChart sx={{ fontSize: 18 }} />
          </IconButton>
          <Tooltip title="View on Finviz">
            <IconButton
              size="small"
              component="a"
              href={`https://finviz.com/quote.ashx?t=${rec.symbol}&p=d`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ p: 0.5 }}
            >
              <img
                src="https://finviz.com/favicon.ico"
                alt="Finviz"
                style={{ width: 14, height: 14 }}
              />
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onClick={() => onInfoOpen(rec.symbol)}
            sx={{ p: 0.5 }}
          >
            <Info sx={{ fontSize: 18 }} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => onDismiss(rec.id)}
            color="default"
            sx={{ p: 0.5 }}
          >
            <Close sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        <Button
          variant="contained"
          size="small"
          color={rec.type === 'exit' ? 'error' : 'success'}
          onClick={() => onExecute(rec)}
          startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
          sx={{ minWidth: 70, fontSize: '0.75rem', px: 1.5, flexShrink: 0 }}
        >
          {rec.type === 'entry' && 'Buy'}
          {rec.type === 'exit' && 'Sell'}
          {rec.type === 'adjustment' && 'Apply'}
          {rec.type === 'alert' && 'OK'}
        </Button>
      </Box>
    </Paper>
  );
};

export default MobileRecommendationCard;
